/**
 * Single source of truth for "which SSO widget/domain are we on".
 *
 * All the actual domain-specific values - which hostname maps to which provider,
 * which widget script to load, which backend endpoint to call - live in
 * domainConfig.json, not here. Adding a new SSO domain later means adding an
 * entry to that JSON file; nothing here or in any of its callers should need an
 * `if` branch added for it.
 */
import domainConfig from "../constants/domainConfig.json";

// hostname can come back missing/non-string in some environments (SSR, or a
// test that mocks window.location without a hostname field) - treat that as
// "no match" rather than throwing, so callers fall back cleanly instead of
// crashing.
const hostIncludes = (hostname, match) => typeof hostname === "string" && hostname.includes(match);

export const getSSOProvider = (hostname = window.location.hostname) => {
  const match = Object.entries(domainConfig.providers).find(([, config]) =>
    hostIncludes(hostname, config.hostnameMatch)
  );
  return match ? match[0] : domainConfig.defaultProvider;
};

export const getSSOConfig = (hostname = window.location.hostname) => {
  return domainConfig.providers[getSSOProvider(hostname)];
};

// The backend API/websocket base URL depends on environment (staging vs
// production) as well as brand (Service Points vs SP-lite), so it's looked up
// separately from the SSO provider above by exact-ish hostname match. Order in
// domainConfig.json matters: each staging host must be listed before its
// production counterpart, since e.g. "staging.email.servicepoints.nl" contains
// "email.servicepoints.nl" as a substring.
const findApiHost = hostname => {
  return domainConfig.apiHosts.find(entry => hostIncludes(hostname, entry.hostnameMatch));
};

// Falls back to the build-time env value when the hostname isn't a known
// deployment (e.g. local dev), so `npm run dev` keeps working unchanged.
export const getApiBaseUrl = (hostname = window.location.hostname) => {
  const match = findApiHost(hostname);
  return match ? match.apiBaseUrl : process.env.REACT_APP_API_URL;
};

export const getWebSocketUrl = (hostname = window.location.hostname) => {
  const match = findApiHost(hostname);
  return match ? match.websocketUrl : process.env.REACT_APP_WEBSOCKET_URL;
};
